import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/** Who put an alias in the table. */
export type CrossReferenceAttributeAliasOrigin = 'llm' | 'manual';

/**
 * WHICH catalog surface the source key names.
 *
 * `customField` is a key on the item's `customFields` map — a column the tenant
 * defined for itself. `itemColumn` is one of the item table's OWN built-in
 * columns (`manufacturer`, `family`, `countryOfOrigin`, …), which carry real
 * product description for every tenant and were previously unreachable by the
 * matcher: the dictionary could only ever link a custom field, so a catalog
 * that keeps its manufacturer in the built-in column had that column compared
 * by nobody.
 *
 * The two are kept apart rather than folded into one key space because they are
 * validated against different lists (a custom-field definition on one side, a
 * curated column list on the other) and because a screen has to be able to say
 * which one a row means.
 */
export type CrossReferenceAttributeAliasSourceKind =
	| 'customField'
	| 'itemColumn'
	| 'requestAttribute';

/**
 * How far a row has travelled from proposal to decision.
 *
 * `provisional`: written by the lookup at emission time, from the model's own
 * reading of a name it met on a page or on a customer line. It routes values
 * from the moment it is written, so matching never waits for an admin, but a
 * contradiction it produces cannot disqualify a candidate on its own (see the
 * fail-safe rule in `docs/claude/product-matching/DESIGN-review-simplification.md`).
 * `confirmed`: a person accepted it, or wrote it by hand. Full strength.
 * `ignored`: a person said this name is not one of our specs. It routes
 * nowhere and stops being reported as unlinked. `canonicalKey` is null.
 */
export type CrossReferenceAttributeAliasStatus =
	| 'provisional'
	| 'confirmed'
	| 'ignored';

/**
 * What the lookup saw when it proposed the row. Kept so the history a person
 * reads later shows the values, not only the names.
 */
export interface CrossReferenceAttributeAliasProposalEvidenceModel {
	/** The value the source stated under the original name. */
	sourceValue: string | null;
	/** Where the source stated it (page URL or `customer-email`). */
	sourceUrl: string | null;
	/** The value our best candidate carried under the canonical key, if any. */
	catalogValue: string | null;
	/** That candidate's item id, if any. */
	catalogItemId: number | null;
	/** The model's one-line reason for the reading. */
	reason: string | null;
}

/**
 * "This catalog attribute key means the same thing as that canonical one."
 *
 * WHY this table exists: the matching engine compares a line's attributes to a
 * catalog item's attributes over the matching profile's CANONICAL keys
 * (`material`, `threadSize`, `coating`, ...). A tenant's own item attributes are
 * keyed in the tenant's own language and spelling (`Werkstoff`,
 * `Gewindegröße`, `Werkstoff_Produktfinder`), so without a key mapping the
 * curated half of a rich catalog is never read: the engine scores on fragments
 * parsed out of the description while the operator's own columns sit unused.
 *
 * It is a PERSISTED DICTIONARY, not a runtime judgement. The rows may be
 * PROPOSED by a model once, as a curation step, but they are consumed
 * deterministically at match time — the scoring loop never guesses that two
 * differently spelled keys are the same attribute. That is what keeps a match
 * explainable: a reviewer asking why `Werkstoff` counted as `material` gets a
 * row with an author and a rationale, not a model's mood on the day.
 *
 * Retiring is a deactivation rather than a delete, exactly as
 * `item_cross_reference` retires a learned equivalence: the row records a
 * decision somebody made, and how the engine used to score is a question the
 * audit trail has to be able to answer.
 */
@Index('uq_craa_source', ['sourceKeyNormalized'], { unique: true })
@Entity('cross_reference_attribute_alias')
export class CrossReferenceAttributeAliasEntity extends BaseEntity {
	/**
	 * The tenant attribute key exactly as it is stored on the item — the string
	 * a person recognises from the custom-field definition screen.
	 */
	@Column('varchar', { name: 'sourceKey', length: 128 })
	public sourceKey!: string;

	/**
	 * Whether `sourceKey` names a tenant custom field or a built-in item column.
	 *
	 * Defaulted at the column so every row written before built-in columns were
	 * linkable reads as what it is — a custom-field alias — with no backfill.
	 *
	 * NOTE on uniqueness: `uq_craa_source` is on the folded source key ALONE, so
	 * one tenant cannot link both a custom field and a built-in column that fold
	 * to the same name. That is a deliberate limitation rather than an oversight:
	 * two rows with the same folded key would be two answers to "what does this
	 * key mean", and the matcher would apply only one of them.
	 */
	@Column('varchar', {
		name: 'sourceKind',
		length: 16,
		default: () => "'customField'",
	})
	public sourceKind!: CrossReferenceAttributeAliasSourceKind;

	/**
	 * The comparison form of `sourceKey`, folded with `normalizeAttributeKey`.
	 *
	 * EVERY lookup goes through this column: the map the matcher consults is
	 * keyed on the folded form, and the uniqueness of an alias is decided on it
	 * too. One fold definition, never two — a second fold would admit two rows
	 * that the matcher then sees as one, and only one of them would ever apply.
	 */
	@Column('varchar', { name: 'sourceKeyNormalized', length: 128 })
	public sourceKeyNormalized!: string;

	/**
	 * The profile attribute key this one means. Validated on write against the
	 * compiled profile's declared attributes: an alias onto a key nothing
	 * compares is a row that can never do anything, and it would read on the
	 * screen as though it were working.
	 */
	@Column('varchar', { name: 'canonicalKey', length: 64, nullable: true })
	public canonicalKey!: string | null;

	/** Proposal → decision state. Column default keeps existing rows confirmed. */
	@Column('varchar', {
		name: 'status',
		length: 16,
		default: () => "'confirmed'",
	})
	public status!: CrossReferenceAttributeAliasStatus;

	/** Provenance: the request line on which the name was first seen. */
	@Column('int', {
		name: 'fk_cross_reference_request_line__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceRequestLineId!: number | null;

	/** Provenance: the looked-up reference product that carried the name. */
	@Column('int', {
		name: 'fk_cross_reference_external_article__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceExternalArticleId!: number | null;

	/** What was on both sides when the row was proposed. Null for hand-written rows. */
	@Column('json', { name: 'proposalEvidence', nullable: true })
	public proposalEvidence!: CrossReferenceAttributeAliasProposalEvidenceModel | null;

	/** Who moved the row out of `provisional`, and when. Null while provisional. */
	@Column('int', { name: 'confirmedByUserId', nullable: true, unsigned: true })
	public confirmedByUserId!: number | null;

	@Column('datetime', { name: 'confirmedAt', nullable: true })
	public confirmedAt!: Date | null;

	/** Whether a model proposed this row or a person wrote it. */
	@Column('varchar', { name: 'origin', length: 16 })
	public origin!: CrossReferenceAttributeAliasOrigin;

	/**
	 * One line saying WHY the two keys mean the same thing.
	 *
	 * This is the explainability payload and the reason the whole table is worth
	 * having: without it an alias is an unaccountable assertion that silently
	 * changes what every future match compares.
	 */
	@Column('varchar', { name: 'rationale', nullable: true, length: 500 })
	public rationale!: string | null;

	/**
	 * Deactivation, never a delete — see the class comment. A retired alias is
	 * excluded from the map the matcher builds, so the engine goes back to
	 * scoring exactly as it did before the row existed.
	 */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;
}
